/*******************************************************
* Login Form Component
* 
* Provides user authentication interface with support for both
* traditional username/password login and Microsoft SSO.
* 
* Key Features:
* - Username/password authentication
* - Microsoft Oauth integration
* - Email domain auto-completion
* - Form validation and error handling
* !!!!!!!! This file could certainly simplified from a CSS standpoint, but I'm not going to time-sink that right now.
********************************************************/
'use client';
//########## IMPORTS ##########
// #region IMPORTS
import { type FormEvent, useCallback, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Typography,
    Container,
    Paper,
    Alert,
    Divider,
    Stack,
} from '@mui/material';
import Image from 'next/image';
import TRUELogo from '@/public/assets/media/Logos/TRUE2 Logo.png';
import MicrosoftLoginButton from '@/src/app/(pages)/Login/components/ButtonMicrosoftLogin';
import AccountReqDialog from '@/src/app/(pages)/Login/components/DialogAccountReq';
import ForgotPasswordDialog from '@/src/app/(pages)/Login/components/DialogForgotPassword';
import { getVersion } from '@/src/scripts/version';
import { FormTextField } from '@/src/components/common/FormTextField';
import useLogin from '@/src/app/(pages)/Login/hooks/UseLogin';
import { commonEmailDomains } from '@/src/scripts/libs/clientUtilLib';
import { tokens, rem } from '@/src/theme/tokens';
// #endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS

// Extracted styles into reusable objects
const styles = {
    container: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
    },
    paper: {
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backdropFilter: 'blur(10px)',
        position: 'relative',
        maxWidth: '100%',
        width: '100%',
        borderRadius: 2
    },
    logoContainer: {
        width: '240px',
        height: '100px',
        position: 'relative',
        mb: 2
    },
    appTitle: {
        fontWeight: 600,
        textAlign: 'center',
        mb: 3
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: '4px',
        },
        '.MuiInputLabel-outlined:has(+ .MuiOutlinedInput-root > .MuiOutlinedInput-input:-webkit-autofill)': {
            transform: 'translate(14px, -9px) scale(0.75)',
        },
        '.MuiOutlinedInput-input:-webkit-autofill + .MuiOutlinedInput-notchedOutline > legend': {
            maxWidth: '100%',
        },
    },
    footerButton: {
        width: '80%'
    },
    versionText: {
        position: 'absolute',
        bottom: 4,
        right: 8,
        opacity: 0.4,
        fontSize: rem(tokens.fontSize.tag),
    }
};
//#endregion


export default function LoginForm() {
    //########## Hooks ##########
    // #region Hooks
    const { login } = useLogin();
    const [accountReqDialogOpen, setAccountReqDialogOpen] = useState(false);
    const [forgotPasswordDialogOpen, setForgotPasswordDialogOpen] = useState(false);
    const [formData, setFormData] = useState({ username: '', password: '' });
    const [error, setError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    //#endregion


    //########## MAIN LOGIC ##########
    // #region MAIN LOGIC
    const handleChange = useCallback((field: string, value: string) => {
        if (isSubmitting) return;
        setFormData({ ...formData, [field]: value });
        setError(null);
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    }, [formData, fieldErrors, isSubmitting]);

    const emailSuggestions = useMemo(() => {
        const value = formData.username;
        if (!value.includes('@')) return [];

        const [localPart, domainPart] = value.split('@');
        return commonEmailDomains
            .filter(d => !domainPart || d.toLowerCase().startsWith(domainPart.toLowerCase()))
            .map(d => `${localPart}@${d}`);
    }, [formData.username]);

    const handleValidation = useCallback(() => {
        setError(null);
        setFieldErrors({});
        const newFieldErrors: Record<string, string> = {};
        if (!formData.username) newFieldErrors.username = 'Username is required';
        if (!formData.password) newFieldErrors.password = 'Password is required';

        if (Object.keys(newFieldErrors).length > 0) {
            setFieldErrors(newFieldErrors);
            return true;
        }
        return false;
    }, [formData]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setError(null);
        setFieldErrors({});

        const validationError = handleValidation();
        if (validationError) {
            return;
        }

        setIsSubmitting(true);

        try {
            const result = await login(formData);

            if (result.success) {
                window.location.href = '/';
            } else {
                setError(result.error || 'An error occurred while logging in');
            }
        } catch (error) {
            console.error('Error logging in:', error);
            setError(`An error occurred while logging in: ${error}`);
        } finally {
            setIsSubmitting(false);
        }
    };
    //#endregion

    //########## MARKDOWN ##########
    // #region MARKDOWN
    return (
        <Container component="main" sx={{ ...styles.container, maxWidth: { tablet: '444px' } }}>
            <Paper elevation={24} sx={styles.paper}>
                <Box sx={styles.logoContainer}>
                    <Image
                        src={TRUELogo}
                        alt="TRUE Logo"
                        sizes='240px'
                        fill
                        style={{ objectFit: 'contain' }}
                        priority
                    />
                </Box>

                <Box
                    component="form"
                    method="post"
                    noValidate
                    onSubmit={handleSubmit}
                    sx={{ mt: 1, width: '100%' }}
                >
                    {error && (
                        <Alert
                            severity="error"
                            sx={{ mb: 2 }}
                            onClose={() => setError(null)}
                        >
                            {error}
                        </Alert>
                    )}

                    <Stack spacing={tokens.space.section} sx={{ justifyContent: 'center', alignItems: 'center' }}>
                        <Autocomplete
                            freeSolo
                            fullWidth
                            disableClearable
                            options={emailSuggestions}
                            inputValue={formData.username}
                            onInputChange={(_, newValue) => handleChange('username', newValue ?? '')}
                            sx={{
                                '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
                                    padding: 0,
                                },
                                '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                                    padding: '16.5px 14px', // rails-ignore: matches MUI's own outlined input so these fields line up
                                },
                            }}
                            renderInput={(params) => (
                                <FormTextField
                                    {...params}
                                    name="username"
                                    label="Employee ID / Email / Username"
                                    error={fieldErrors.username}
                                    required
                                    autoFocus
                                    sx={styles.textField}
                                />
                            )}
                        />
                        <FormTextField
                            name="password"
                            label="Password"
                            type="password"
                            value={formData.password}
                            onChange={handleChange}
                            error={fieldErrors.password}
                            required
                            fullWidth
                            sx={styles.textField}
                        />
                        <Box sx={{ alignSelf: 'flex-start' }}>
                            <Button
                                variant="text"
                                size="small"
                                onClick={() => setForgotPasswordDialogOpen(true)}
                            >
                                Forgot Password?
                            </Button>
                        </Box>

                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            size="large"
                            sx={styles.footerButton}
                            disabled={isSubmitting}
                            loading={isSubmitting}
                            loadingPosition='end'
                        >
                            Sign In
                        </Button>


                        <Divider sx={{ width: '100%' }}>
                            <Typography variant="body2" color="text.primary">
                                or
                            </Typography>
                        </Divider>


                        <Box sx={styles.footerButton}>
                            <MicrosoftLoginButton />
                        </Box>

                        <Divider sx={{ width: '100%' }}>
                            <Typography variant="body2" color="text.primary">
                                New User?
                            </Typography>
                        </Divider>

                        <Button
                            variant="outlined"
                            color="primary"
                            size="large"
                            onClick={() => setAccountReqDialogOpen(true)}
                            sx={styles.footerButton}
                        >
                            Request An Account
                        </Button>

                        <Typography variant="caption" sx={styles.versionText}>
                            v{getVersion()}
                        </Typography>
                    </Stack>
                </Box>


            </Paper>

            <AccountReqDialog
                open={accountReqDialogOpen}
                handleClose={() => setAccountReqDialogOpen(false)}
                setMainDialogOpen={setAccountReqDialogOpen}
            />

            <ForgotPasswordDialog
                open={forgotPasswordDialogOpen}
                onClose={() => setForgotPasswordDialogOpen(false)}
                setMainDialogOpen={setForgotPasswordDialogOpen}
            />
        </Container>
    );
    //#endregion
}
